/**
 * Custom select component — progressive enhancement voor native
 * `<select>` elementen gemarkeerd met `data-agora-select`.
 *
 * Pattern: native select blijft in de DOM als source-of-truth (form
 * participation, change-events, no-JS fallback), custom ARIA-listbox
 * UI eromheen spiegelt de state. Selectie in de custom UI synct naar
 * native via `element.value = …` + `dispatchEvent(new Event('change'))`;
 * externe wijzigingen aan `native.value` worden via een change-listener
 * teruggespiegeld naar de custom UI (bidirectioneel, met internal-guard
 * tegen infinite loops).
 *
 * Keyboard-navigatie (ARIA 1.2 listbox pattern):
 *   Enter / Space  — toggle / select
 *   ArrowUp/Down   — beweeg active descendant
 *   Home / End     — eerste / laatste optie
 *   Escape         — sluit listbox
 *   [printable]    — type-ahead (500 ms buffer)
 *
 * Idempotent: al-enhanced selects worden geskipt via
 * `[data-agora-select-enhanced]`. Veilig om `initCustomSelects()`
 * meermaals aan te roepen — zoals na elke AJAX DOM-update.
 *
 * @module modules/ui/custom-select
 */

/**
 * Enhance één native `<select>`. No-op wanneer al enhanced.
 *
 * @param {HTMLSelectElement} selectElement
 */
export function enhanceSelect(selectElement) {
    if (!(selectElement instanceof HTMLSelectElement)) return;
    if (selectElement.dataset.agoraSelectEnhanced === 'true') return;

    const options = Array.from(selectElement.options).map((opt, index) => ({
        value: opt.value,
        label: (opt.textContent || '').trim(),
        selected: opt.selected,
        disabled: opt.disabled,
        index,
    }));
    const selectedOption = options.find((o) => o.selected) || options[0] || null;

    const wrapper = document.createElement('div');
    wrapper.className = 'agora-select';
    wrapper.setAttribute('data-agora-select-wrapper', '');

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'agora-select__trigger';
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-haspopup', 'listbox');

    const valueSpan = document.createElement('span');
    valueSpan.className = 'agora-select__value';
    valueSpan.textContent = selectedOption ? selectedOption.label : '';

    const indicator = document.createElement('span');
    indicator.className = 'agora-select__indicator';
    indicator.setAttribute('aria-hidden', 'true');

    trigger.appendChild(valueSpan);
    trigger.appendChild(indicator);

    const listbox = document.createElement('ul');
    listbox.className = 'agora-select__listbox';
    listbox.setAttribute('role', 'listbox');
    listbox.hidden = true;
    const listboxId = `agora-select-listbox-${Math.random().toString(36).slice(2, 8)}`;
    listbox.id = listboxId;
    trigger.setAttribute('aria-controls', listboxId);

    options.forEach((opt) => {
        const li = document.createElement('li');
        li.className = 'agora-select__option';
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', opt.selected ? 'true' : 'false');
        li.dataset.value = opt.value;
        li.dataset.index = String(opt.index);
        li.id = `${listboxId}-option-${opt.index}`;
        li.textContent = opt.label;
        if (opt.disabled) li.setAttribute('aria-disabled', 'true');
        if (opt.selected) li.classList.add('agora-select__option--selected');
        listbox.appendChild(li);
    });

    // Wrap het native select zodat DOM-volgorde blijft:
    // <wrapper>
    //   <trigger/>
    //   <listbox/>
    //   <select hidden/>  ← native blijft form-child
    // </wrapper>
    selectElement.parentNode.insertBefore(wrapper, selectElement);
    wrapper.appendChild(trigger);
    wrapper.appendChild(listbox);
    wrapper.appendChild(selectElement);

    selectElement.hidden = true;
    selectElement.setAttribute('tabindex', '-1');
    selectElement.setAttribute('aria-hidden', 'true');
    selectElement.dataset.agoraSelectEnhanced = 'true';

    // Optionele backdrop: wanneer het native select
    // `data-agora-select-backdrop` heeft, plaatsen we een fixed dim-overlay
    // onder de listbox. Click op de backdrop sluit de listbox.
    let backdrop = null;
    if (selectElement.hasAttribute('data-agora-select-backdrop')) {
        backdrop = document.createElement('div');
        backdrop.className = 'agora-select__backdrop';
        backdrop.hidden = true;
        wrapper.appendChild(backdrop);
    }

    // Stabiele trigger-breedte: meet de breedte die nodig
    // is voor de LANGSTE optie en zet die als `min-width` op de
    // trigger. Anders krimpt de trigger mee met korte selecties zoals
    // "Nieuwste" en breken langere opties zoals "Alfabetisch op titel"
    // af in de listbox. We meten via `trigger.scrollWidth`: het
    // element zit al in de DOM met geapplied CSS (padding, gap,
    // indicator-breedte zitten dus mee in de meting). De valueSpan
    // heeft `white-space: nowrap` zodat scrollWidth de echte content-
    // breedte teruggeeft, niet de visible breedte met ellipsis.
    const originalLabel = valueSpan.textContent;
    let maxWidth = 0;
    options.forEach((opt) => {
        valueSpan.textContent = opt.label;
        if (trigger.scrollWidth > maxWidth) {
            maxWidth = trigger.scrollWidth;
        }
    });
    valueSpan.textContent = originalLabel;
    if (maxWidth > 0) {
        trigger.style.minWidth = `${maxWidth}px`;
    }

    bindSelectEvents(wrapper, trigger, listbox, selectElement, backdrop);
}

/**
 * Init / re-init alle `[data-agora-select]` elementen op de pagina.
 * Idempotent dankzij de `data-agora-select-enhanced` flag op reeds
 * verwerkte selects.
 */
export function initCustomSelects() {
    document
        .querySelectorAll('select[data-agora-select]:not([data-agora-select-enhanced])')
        .forEach((select) => {
            enhanceSelect(/** @type {HTMLSelectElement} */ (select));
        });
}

/**
 * Event-binding + keyboard-navigatie per enhanced select.
 */
function bindSelectEvents(wrapper, trigger, listbox, nativeSelect, backdrop = null) {
    let activeIndex = -1;
    let typeAheadBuffer = '';
    let typeAheadTimer = null;
    // Guard tegen recursie: `selectOption` dispatcht een `change`-event
    // op het native select, maar diezelfde listener zou dan de custom
    // UI opnieuw proberen te syncen → dubbel werk. Flag onderbreekt dat.
    let isInternalChange = false;

    function getEnabledOptions() {
        return Array.from(
            listbox.querySelectorAll('[role="option"]:not([aria-disabled="true"])'),
        );
    }

    function setActiveDescendant(option) {
        listbox.querySelectorAll('[role="option"]').forEach((o) => {
            o.classList.remove('agora-select__option--active');
        });
        if (!option) {
            trigger.removeAttribute('aria-activedescendant');
            return;
        }
        option.classList.add('agora-select__option--active');
        trigger.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
    }

    function positionListbox() {
        listbox.style.removeProperty('top');
        listbox.style.removeProperty('bottom');
        listbox.style.removeProperty('left');
        listbox.style.removeProperty('right');

        const rect = listbox.getBoundingClientRect();
        if (rect.bottom > window.innerHeight) {
            listbox.style.top = 'auto';
            listbox.style.bottom = 'calc(100% + 2px)';
        }
        if (rect.right > window.innerWidth) {
            listbox.style.left = 'auto';
            listbox.style.right = '0';
        }
    }

    function openListbox() {
        trigger.setAttribute('aria-expanded', 'true');
        // Eerst `hidden` weghalen zodat het element in de render-tree
        // staat; daarna `is-open` toevoegen op het volgende frame zodat
        // de CSS transitie van opacity/visibility/transform daadwerkelijk
        // start vanuit de hidden-state. Zonder rAF zien we geen animatie
        // omdat de browser beide style-mutaties batcht.
        listbox.removeAttribute('hidden');
        if (backdrop) backdrop.removeAttribute('hidden');
        requestAnimationFrame(() => {
            listbox.classList.add('is-open');
            if (backdrop) backdrop.classList.add('is-open');
        });

        const selected = listbox.querySelector('[aria-selected="true"]');
        const enabled = getEnabledOptions();
        if (selected) {
            activeIndex = enabled.indexOf(selected);
            setActiveDescendant(selected);
        } else if (enabled[0]) {
            activeIndex = 0;
            setActiveDescendant(enabled[0]);
        }
        positionListbox();
    }

    function closeListbox() {
        trigger.setAttribute('aria-expanded', 'false');
        listbox.classList.remove('is-open');
        if (backdrop) backdrop.classList.remove('is-open');
        // `hidden` pas zetten ná de CSS transitie zodat de fade-out
        // zichtbaar is. 200ms = transitie-duur (150ms) + safety-marge.
        // Een nieuwe open-call binnen dat venster annuleert de hide.
        const expectedExpanded = trigger.getAttribute('aria-expanded');
        setTimeout(() => {
            if (trigger.getAttribute('aria-expanded') !== expectedExpanded) return;
            if (trigger.getAttribute('aria-expanded') === 'false') {
                listbox.setAttribute('hidden', '');
                if (backdrop) backdrop.setAttribute('hidden', '');
            }
        }, 200);
        activeIndex = -1;
        setActiveDescendant(null);
    }

    function syncCustomFromNative() {
        const value = nativeSelect.value;
        const matched = listbox.querySelector(`[data-value="${CSS.escape(value)}"]`);
        listbox.querySelectorAll('[role="option"]').forEach((o) => {
            const isMatch = o === matched;
            o.setAttribute('aria-selected', isMatch ? 'true' : 'false');
            o.classList.toggle('agora-select__option--selected', isMatch);
        });
        if (matched) {
            wrapper.querySelector('.agora-select__value').textContent =
                matched.textContent.trim();
        }
    }

    function selectOption(option) {
        const value = option.dataset.value || '';
        const label = (option.textContent || '').trim();

        wrapper.querySelector('.agora-select__value').textContent = label;
        listbox.querySelectorAll('[role="option"]').forEach((o) => {
            const isMatch = o === option;
            o.setAttribute('aria-selected', isMatch ? 'true' : 'false');
            o.classList.toggle('agora-select__option--selected', isMatch);
        });

        isInternalChange = true;
        nativeSelect.value = value;
        nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        isInternalChange = false;

        closeListbox();
        trigger.focus();
    }

    trigger.addEventListener('click', () => {
        if (trigger.getAttribute('aria-expanded') === 'true') {
            closeListbox();
        } else {
            openListbox();
        }
    });

    listbox.addEventListener('click', (e) => {
        const option = /** @type {HTMLElement|null} */ (
            e.target instanceof Element ? e.target.closest('[role="option"]') : null
        );
        if (!option) return;
        if (option.getAttribute('aria-disabled') === 'true') return;
        selectOption(option);
    });

    // Backdrop click sluit de listbox. Backdrop zit in wrapper én is
    // position:fixed; zonder deze handler zou het document-listener
    // pad de click ook opvangen, maar expliciet hier is duidelijker
    // én het werkt ook wanneer de event-target precies de backdrop is.
    if (backdrop) {
        backdrop.addEventListener('click', () => {
            if (trigger.getAttribute('aria-expanded') === 'true') closeListbox();
        });
    }

    document.addEventListener('click', (e) => {
        if (!(e.target instanceof Node)) return;
        // Backdrop afgehandeld door eigen listener hierboven; hij zit in
        // de wrapper dus zou anders door de containment-check als "binnen"
        // worden geclassificeerd.
        if (backdrop && backdrop.contains(e.target)) return;
        if (!wrapper.contains(e.target)) {
            if (trigger.getAttribute('aria-expanded') === 'true') closeListbox();
        }
    });

    trigger.addEventListener('keydown', (e) => {
        const enabled = getEnabledOptions();
        if (enabled.length === 0) return;

        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (trigger.getAttribute('aria-expanded') === 'true') {
                    if (activeIndex >= 0 && enabled[activeIndex]) {
                        selectOption(enabled[activeIndex]);
                    }
                } else {
                    openListbox();
                }
                break;

            case 'ArrowDown':
                e.preventDefault();
                if (trigger.getAttribute('aria-expanded') !== 'true') {
                    openListbox();
                } else {
                    activeIndex = Math.min(activeIndex + 1, enabled.length - 1);
                    setActiveDescendant(enabled[activeIndex]);
                }
                break;

            case 'ArrowUp':
                e.preventDefault();
                if (trigger.getAttribute('aria-expanded') !== 'true') {
                    openListbox();
                } else {
                    activeIndex = Math.max(activeIndex - 1, 0);
                    setActiveDescendant(enabled[activeIndex]);
                }
                break;

            case 'Home':
                if (trigger.getAttribute('aria-expanded') === 'true') {
                    e.preventDefault();
                    activeIndex = 0;
                    setActiveDescendant(enabled[0]);
                }
                break;

            case 'End':
                if (trigger.getAttribute('aria-expanded') === 'true') {
                    e.preventDefault();
                    activeIndex = enabled.length - 1;
                    setActiveDescendant(enabled[activeIndex]);
                }
                break;

            case 'Escape':
                if (trigger.getAttribute('aria-expanded') === 'true') {
                    e.preventDefault();
                    closeListbox();
                    trigger.focus();
                }
                break;

            default:
                if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                    handleTypeAhead(e.key, enabled);
                }
                break;
        }
    });

    function handleTypeAhead(char, enabled) {
        if (typeAheadTimer !== null) clearTimeout(typeAheadTimer);
        typeAheadBuffer += char.toLowerCase();
        typeAheadTimer = setTimeout(() => {
            typeAheadBuffer = '';
            typeAheadTimer = null;
        }, 500);

        const match = enabled.findIndex((o) =>
            (o.textContent || '').trim().toLowerCase().startsWith(typeAheadBuffer),
        );
        if (match >= 0) {
            activeIndex = match;
            setActiveDescendant(enabled[match]);
            if (trigger.getAttribute('aria-expanded') !== 'true') openListbox();
        }
    }

    // Externe change — bv. via `syncFormFromUrl` (popstate) of
    // programmatische `element.value = …` — houdt de custom UI mee.
    nativeSelect.addEventListener('change', () => {
        if (isInternalChange) return;
        syncCustomFromNative();
    });
}
